import { useEffect, useState } from "react";
import { FiArrowDown, FiMenu, FiX } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const navLinks = [
  { title: "Home", path: "/" },
  { title: "Skills", sectionId: "skills" },
  { title: "About", sectionId: "about" },
  { title: "Projects", sectionId: "projects" },
  { title: "Contact", sectionId: "contact" },
];

const AppHeader = () => {
  const [showMenu, setShowMenu] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const updateScrolledState = () => {
      setHasScrolled(window.scrollY > 12);
    };

    updateScrolledState();
    window.addEventListener("scroll", updateScrolledState, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateScrolledState);
    };
  }, []);

  const closeMenu = () => setShowMenu(false);

  const toggleMenu = () => {
    setShowMenu((isVisible) => !isVisible);
  };

  const scrollToSection = (sectionId) => {
    closeMenu();

    if (location.pathname !== "/") {
      navigate(`/#${sectionId}`);
      return;
    }

    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    window.history.replaceState(null, "", `#${sectionId}`);
  };

  const handleNavClick = (link) => {
    if (link.sectionId) {
      scrollToSection(link.sectionId);
      return;
    }

    closeMenu();
    if (link.path === "/" && location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      window.history.replaceState(null, "", "/");
      return;
    }

    navigate(link.path);
  };

  const isActivePath = (path) => {
    if (!path) return false;

    return path === "/"
      ? location.pathname === "/" && !location.hash
      : location.pathname === path;
  };

  return (
    <motion.header
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      id="nav"
      className={`sticky top-0 z-30 flex justify-end bg-white px-4 py-5 text-black transition-shadow duration-300 sm:px-6 lg:px-10 ${
        hasScrolled ? "shadow-[0_10px_28px_rgba(0,0,0,0.08)]" : "shadow-none"
      }`}
    >
      <div className="hidden items-stretch justify-center gap-1.5 md:flex">
        <nav
          className="flex min-h-[65px] flex-1 items-center justify-center gap-2 rounded-[3rem] rounded-tb-r-xl border-2 border-black bg-white px-2.8"
          aria-label="Main navigation"
        >
          {navLinks.map((link) => (
            <button
              key={link.title}
              type="button"
              onClick={() => handleNavClick(link)}
              className={`rounded-[22px] px-5 py-2 font-general-semibold text-xl outline-none transition hover:bg-black hover:text-white lg:px-8 ${
                isActivePath(link.path)
                  ? "bg-black text-white"
                  : "bg-white text-black"
              }`}
            >
              {link.title}
            </button>
          ))}
        </nav>

        <a
          download="Rahul_Ganesh_Resume.pdf"
          href="/files/Rahul_Ganesh_Resume.pdf"
          className="inline-flex min-h-[65px] items-center justify-center gap-3 rounded-tb-l-xl border-2 border-black bg-black px-6 font-general-semibold text-2xl text-white transition hover:bg-white hover:text-black lg:px-14"
          aria-label="Download Resume"
        >
          Resume
          <FiArrowDown className="h-5 w-5" />
        </a>
      </div>

      <div className="mx-auto flex max-w-[1700px] items-center justify-between gap-3 md:hidden">
        <button
          type="button"
          onClick={() => handleNavClick(navLinks[0])}
          className="rounded-[20px] border-2 border-black bg-black px-5 py-3 font-general-semibold text-white"
          aria-label="Rahul Ganesh Home"
        >
          Home
        </button>

        <div className="flex items-center gap-2">
          <a
            download="Rahul_Ganesh_Resume.pdf"
            href="/files/Rahul_Ganesh_Resume.pdf"
            className="inline-flex items-center gap-2 rounded-[18px] border-2 border-black bg-black px-4 py-3 font-general-semibold text-white"
            aria-label="Download Resume"
          >
            Resume
            <FiArrowDown className="h-4 w-4" />
          </a>
          <button
            onClick={toggleMenu}
            type="button"
            className="grid h-12 w-12 place-items-center rounded-[18px] border-2 border-black bg-white text-black"
            aria-label="Toggle Menu"
          >
            {showMenu ? (
              <FiX className="text-2xl" />
            ) : (
              <FiMenu className="text-2xl" />
            )}
          </button>
        </div>
      </div>

      {showMenu && (
        <nav
          className="mx-auto mt-4 grid max-w-[1700px] gap-1 rounded-[24px] border-2 border-black bg-white p-2 font-general-semibold md:hidden"
          aria-label="Mobile navigation"
        >
          {navLinks.map((link) => (
            <button
              key={link.title}
              type="button"
              onClick={() => handleNavClick(link)}
              className={`rounded-[18px] px-4 py-3 text-left transition hover:bg-black hover:text-white ${
                isActivePath(link.path)
                  ? "bg-black text-white"
                  : "bg-white text-black"
              }`}
            >
              {link.title}
            </button>
          ))}
        </nav>
      )}
    </motion.header>
  );
};

export default AppHeader;
